import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';

interface PhotoLightboxProps {
  /** Presigned image URL to display full-size. */
  src: string;
  /** Accessible label / alt for the image (e.g. the patient's name). */
  alt: string;
  onClose: () => void;
}

/**
 * Full-screen viewer for a single photo. Backdrop click, the close button and
 * the Escape key all dismiss it; the image is centred and bounded to the
 * viewport so portrait and landscape captures both fit without cropping. Body
 * scroll is locked while open so the page underneath doesn't move.
 */
export function PhotoLightbox({ src, alt, onClose }: PhotoLightboxProps) {
  const { t } = useTranslation();

  // Esc-to-close + scroll lock, both torn down on unmount so we never leave the
  // body stuck or a stray listener attached.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 sm:p-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <Icon name="x" size={20} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
      />
    </div>
  );
}
